import '../i18n';
import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from 'apollo-boost';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { PALE_WHITE_1, PALE_WHITE_6, WHITE } from '../appearance/Colors';

export const GET_ORIGINAL_EMAIL = gql`
    query OriginalEmail($data: GetMessageInput!) {
        getMessage(data: $data, trackView: false) { id email { subject from receivedAt preview hasOriginalHtml text originalHtml } }
    }
`;

export default function EmailReaderContent({ enabled = true, messageId, children, onTextLoaded, view, fullHeight = false, showControls = true }) {
    if (!enabled || !messageId) return children;
    return <OriginalEmailReader key={messageId} messageId={messageId} onTextLoaded={onTextLoaded} view={view} fullHeight={fullHeight} showControls={showControls}>{children}</OriginalEmailReader>;
}

function OriginalEmailReader({ messageId, children, onTextLoaded, view: controlledView, fullHeight, showControls }) {
    const { t } = useTranslation();
    const [localView, setView] = useState('original');
    const view = controlledView || localView;
    const { data, loading, error, refetch } = useQuery(GET_ORIGINAL_EMAIL, {
        variables: { data: { id: messageId } }, fetchPolicy: 'network-only'
    });
    const email = data?.getMessage?.id === messageId ? data.getMessage.email : null;
    React.useEffect(() => { onTextLoaded?.(email?.text || ''); }, [email?.text, onTextLoaded]);
    const html = email?.originalHtml;
    const clean = typeof children === 'function' ? children(email?.text || '') : children;
    return <Canvas $fullHeight={fullHeight}>
        {showControls ? <EmailReaderTabs view={view} onChange={setView} /> : null}
        {loading ? <Status>{t('settings:emailOriginalLoading')}</Status> : error ?
            <Status>{t('settings:emailOriginalFailed')} <button type="button" onClick={() => refetch()}>{t('common:retry')}</button></Status> :
            view === 'clean' ? <CleanCanvas $fullHeight={fullHeight}>{clean}</CleanCanvas> : html ? <Frame $fullHeight={fullHeight} title={t('settings:emailOriginal')} srcDoc={html}
                sandbox="allow-popups allow-popups-to-escape-sandbox" referrerPolicy="no-referrer" /> :
            <><Status>{t('settings:emailOriginalUnavailable')}</Status><CleanCanvas $fullHeight={fullHeight}>{clean}</CleanCanvas></>}
    </Canvas>;
}

export function EmailReaderTabs({ view, onChange }) {
    const { t } = useTranslation();
    return <ReaderTabGroup aria-label={t('settings:emailView')}>
        <button type="button" aria-pressed={view === 'original'} onClick={() => onChange('original')}>
            {t('settings:emailOriginal')}
        </button>
        <button type="button" aria-pressed={view === 'clean'} onClick={() => onChange('clean')}>
            {t('settings:emailCleanReader')}
        </button>
    </ReaderTabGroup>;
}
const Canvas = styled.section`
    width: 100%; min-width: 0;
    height: ${props => props.$fullHeight ? '100%' : 'auto'};
    min-height: 0;
    display: flex; flex-direction: column;
`;
export const ReaderTabGroup = styled.div`
    display: inline-flex; align-items: center; gap: 2px; flex: 0 0 auto;
    width: fit-content; max-width: 100%; padding: 3px;
    border: 1px solid ${PALE_WHITE_1}; border-radius: 12px;
    button {
        display: inline-flex; align-items: center; justify-content: center;
        border: none; background: transparent; color: ${PALE_WHITE_6};
        font-size: 12px; font-weight: 500; padding: 0 12px; height: 28px;
        border-radius: 9px; cursor: pointer; flex-shrink: 0;
        transition: background .15s ease, color .15s ease;
    }
    button[aria-pressed='true'], button[aria-selected='true'] { background: ${PALE_WHITE_1}; color: ${WHITE}; font-weight: 600; }
    button:hover { background: ${PALE_WHITE_1}; color: ${WHITE}; }
    button:focus-visible { outline: 2px solid #7ab0ff; outline-offset: 1px; }
`;
const CleanCanvas = styled.div`
    min-height: 0;
    flex: ${props => props.$fullHeight ? '1 1 auto' : 'initial'};
    overflow-y: ${props => props.$fullHeight ? 'auto' : 'visible'};
`;
const Status = styled.p`padding: 14px; color: #b9c1c9; font-size: 13px;`;
const Frame = styled.iframe`
    display: block; width: 100%;
    height: ${props => props.$fullHeight ? '100%' : '68vh'};
    flex: ${props => props.$fullHeight ? '1 1 auto' : 'initial'};
    min-height: ${props => props.$fullHeight ? '0' : '380px'};
    border: 0; background: #fff; color-scheme: light;
`;
